/*
 * A first-party id for the claim page, so "did the organizer come back?" has
 * an answer.
 *
 * The funnel used to identify a visitor by their address and browser, which
 * made one person on a rotating home connection look like three - the number
 * the report exists to give was wrong by more than it was right. This id is
 * minted here, stored here, and sent only to our own claim endpoint.
 *
 * It is not an identity. It says nothing about who the organizer is, it is
 * scoped to the event server-side, and clearing site data ends it.
 */

const STORAGE_KEY = 'nx2u.claim.visitor';

function mint(): string {
  // Aliased because narrowing `crypto` on the randomUUID check leaves the
  // fallback branch with a type that has no getRandomValues either.
  const source: Crypto = crypto;
  if (typeof source.randomUUID === 'function') {
    return source.randomUUID();
  }
  // Older Safari has crypto but not randomUUID.
  const bytes = new Uint8Array(16);
  source.getRandomValues(bytes);
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes)
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
  return [
    hex.slice(0, 8),
    hex.slice(8, 12),
    hex.slice(12, 16),
    hex.slice(16, 20),
    hex.slice(20),
  ].join('-');
}

/**
 * The stored id, minting one on first use.
 *
 * Returns null rather than throwing when storage is unavailable - private
 * windows and locked-down browsers both refuse it, and an organizer whose
 * browser will not remember them should still be able to claim their event.
 * The server falls back to its old key in that case.
 */
export function claimVisitorId(): string | null {
  try {
    const existing = localStorage.getItem(STORAGE_KEY);
    if (existing) return existing;
    const fresh = mint();
    localStorage.setItem(STORAGE_KEY, fresh);
    return fresh;
  } catch {
    return null;
  }
}

/** Ready to spread into an HttpClient call; empty when storage said no. */
export function claimVisitorHeaders(): Record<string, string> {
  const id = claimVisitorId();
  return id ? { 'X-NX2U-Visitor': id } : {};
}
